// src/components/UploadModal.jsx
import { useState, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";

// 암호학적으로 안전한 난수 생성 (crypto.getRandomValues)
function generateSlug() {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  const arr = new Uint8Array(12);
  crypto.getRandomValues(arr);
  return Array.from(arr).map((b) => chars[b % chars.length]).join("");
}

function formatBytes(bytes) {
  if (bytes === 0) return "0 MB";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export default function UploadModal({
  onClose,
  onSuccess,
  storageUsedBytes = 0,
  storageLimitBytes = 4.8 * 1024 * 1024 * 1024,
}) {
  const [files, setFiles] = useState([]);
  const [diagDate, setDiagDate] = useState("");
  const [client, setClient] = useState("");
  const [capacity, setCapacity] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const fileInputRef = useRef();

  // ── Storage remaining ─────────────────────────────────
  const newFilesSize = files.reduce((s, f) => s + f.size, 0);
  const storageAfterUpload = storageUsedBytes + newFilesSize;
  const storageRemaining = storageLimitBytes - storageUsedBytes;
  const wouldExceed = storageAfterUpload > storageLimitBytes;
  const nearLimit = storageRemaining < 200 * 1024 * 1024; // warn if < 200 MB left

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files).filter(
      (f) => f.type === "application/pdf"
    );
    setFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name));
      const newOnes = selected.filter((f) => !existing.has(f.name));
      return [...prev, ...newOnes];
    });
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(files);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setFiles(reordered);
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleUpload = async () => {
    if (!diagDate || !client || !capacity) {
      setError("진단 날짜, 고객사, 수전용량을 모두 입력해주세요.");
      return;
    }
    if (files.length === 0) {
      setError("PDF 파일을 1개 이상 선택해주세요.");
      return;
    }

    // ── Storage limit check ───────────────────────────
    if (wouldExceed) {
      setError(
        `저장 공간이 부족합니다. 남은 용량: ${formatBytes(storageRemaining)}, 선택한 파일 크기: ${formatBytes(newFilesSize)}`
      );
      return;
    }

    setUploading(true);
    setError("");
    const slug = generateSlug();
    const title = `${diagDate}_${client}_${capacity}`;
    const uploadedFiles = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const storagePath = `posts/${slug}/${file.name}`;
        const storageRef = ref(storage, storagePath);
        await new Promise((resolve, reject) => {
          const task = uploadBytesResumable(storageRef, file);
          task.on(
            "state_changed",
            (snap) => {
              const overall = ((i + snap.bytesTransferred / snap.totalBytes) / files.length) * 100;
              setProgress(Math.round(overall));
            },
            reject,
            async () => {
              const url = await getDownloadURL(task.snapshot.ref);
              // ✅ size 필드 추가 — 스토리지 사용량 계산에 사용
              uploadedFiles.push({ name: file.name, url, order: i, size: file.size });
              resolve();
            }
          );
        });
      }

      await addDoc(collection(db, "posts"), {
        slug,
        title,
        diagDate,
        client,
        capacity,
        files: uploadedFiles,
        createdAt: serverTimestamp(),
      });

      onSuccess();
    } catch (err) {
      console.error(err);
      setError("업로드 중 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <span style={styles.headerTitle}>게시물 등록</span>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.body}>

          {/* ── Storage remaining notice ── */}
          <div
            style={{
              ...styles.storageNotice,
              background: nearLimit ? "rgba(239,68,68,0.06)" : "rgba(30,111,217,0.05)",
              border: `1px solid ${nearLimit ? "rgba(239,68,68,0.3)" : "rgba(30,111,217,0.2)"}`,
            }}
          >
            <span style={{ ...styles.storageNoticeLabel, color: nearLimit ? "#dc2626" : "#1e6fd9" }}>
              {nearLimit ? "⚠ 용량 부족" : "💾 남은 저장 공간"}
            </span>
            <span style={{ ...styles.storageNoticeValue, color: nearLimit ? "#dc2626" : "#374151" }}>
              {formatBytes(storageRemaining)}
            </span>
            {files.length > 0 && (
              <span
                style={{
                  ...styles.storageNoticeNew,
                  color: wouldExceed ? "#dc2626" : "#6b7280",
                }}
              >
                {wouldExceed ? "⚠ " : ""}선택 파일: {formatBytes(newFilesSize)}
              </span>
            )}
          </div>

          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>진단 날짜</label>
              <input
                style={styles.input}
                type="date"
                value={diagDate}
                onChange={(e) => setDiagDate(e.target.value)}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>고객사</label>
              <input
                style={styles.input}
                type="text"
                placeholder="예) 삼성전자"
                value={client}
                onChange={(e) => setClient(e.target.value)}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>수전용량 (kW)</label>
              <input
                style={styles.input}
                type="text"
                placeholder="예) 500kW"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
              />
            </div>
          </div>

          <div
            style={{
              ...styles.fileZone,
              borderColor: wouldExceed ? "#fca5a5" : "#fde68a",
              background: wouldExceed ? "#fff5f5" : "#fefce8",
              cursor: wouldExceed ? "not-allowed" : "pointer",
            }}
            onClick={() => !wouldExceed && fileInputRef.current.click()}
          >
            <div style={styles.fileIcon}>📄</div>
            <div style={styles.fileZoneText}>PDF 파일을 클릭하여 선택</div>
            <div style={styles.fileZoneSub}>다중 선택 가능 · PDF 전용</div>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              multiple
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
          </div>

          {files.length > 0 && (
            <div style={styles.fileListWrap}>
              <div style={styles.fileListHeader}>
                <span style={styles.label}>선택된 파일 ({files.length}개 · {formatBytes(newFilesSize)})</span>
                <span style={styles.fileListHint}>↕ 드래그로 순서 변경</span>
              </div>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="files">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {files.map((file, idx) => (
                        <Draggable key={file.name} draggableId={file.name} index={idx}>
                          {(prov, snapshot) => (
                            <div
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              style={{
                                ...styles.fileItem,
                                background: snapshot.isDragging ? "#fef9c3" : "#ffffff",
                                ...prov.draggableProps.style,
                              }}
                            >
                              <span {...prov.dragHandleProps} style={styles.dragHandle}>⠿</span>
                              <span style={styles.fileNum}>{idx + 1}</span>
                              <span style={styles.fileName}>{file.name}</span>
                              <span style={styles.fileSize}>
                                {formatBytes(file.size)}
                              </span>
                              <button
                                style={styles.removeBtn}
                                onClick={() => removeFile(idx)}
                              >✕</button>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          )}

          {uploading && (
            <div style={styles.progressWrap}>
              <div style={styles.progressBar}>
                <div style={{ ...styles.progressFill, width: `${progress}%` }} />
              </div>
              <span style={styles.progressText}>{progress}% 업로드 중...</span>
            </div>
          )}

          {error && <div style={styles.error}>{error}</div>}
        </div>

        <div style={styles.footer}>
          {/* 취소 버튼: 붉은색 */}
          <button
            style={{ ...styles.btn, ...styles.cancelBtn }}
            onClick={onClose}
            disabled={uploading}
          >
            취소
          </button>
          {/* 업로드(등록) 버튼: 파란색 — 용량 초과 시 비활성화 */}
          <button
            style={{
              ...styles.btn,
              ...styles.uploadBtn,
              opacity: uploading || wouldExceed ? 0.5 : 1,
              cursor: wouldExceed ? "not-allowed" : "pointer",
            }}
            onClick={handleUpload}
            disabled={uploading || wouldExceed}
            title={wouldExceed ? "저장 공간이 부족합니다." : "업로드"}
          >
            {uploading ? "업로드 중..." : "업로드"}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    backdropFilter: "blur(4px)",
  },
  modal: {
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "680px",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 30px 80px rgba(0,0,0,0.15)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px",
    borderBottom: "1px solid #fde68a",
  },
  headerTitle: { color: "#111827", fontSize: "16px", fontWeight: "700" },
  closeBtn: { background: "none", border: "none", color: "#6b7280", fontSize: "16px", cursor: "pointer", padding: "4px" },
  body: { padding: "24px", overflowY: "auto", flex: 1 },

  // ── Storage notice ──
  storageNotice: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    borderRadius: "8px",
    padding: "10px 14px",
    marginBottom: "18px",
    flexWrap: "wrap",
  },
  storageNoticeLabel: { fontSize: "11px", fontWeight: "700", letterSpacing: "0.05em", textTransform: "uppercase" },
  storageNoticeValue: { fontSize: "13px", fontWeight: "700", marginLeft: "auto" },
  storageNoticeNew: { fontSize: "12px", fontWeight: "600" },

  row: { display: "flex", gap: "12px", marginBottom: "20px", flexWrap: "wrap" },
  field: { flex: 1, minWidth: "160px" },
  label: { display: "block", color: "#6b7280", fontSize: "11px", fontWeight: "600", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "6px" },
  input: { width: "100%", boxSizing: "border-box", background: "#ffffff", border: "1px solid #d1d5db", borderRadius: "6px", padding: "10px 12px", color: "#111827", fontSize: "13px", outline: "none", fontFamily: "inherit" },
  fileZone: { border: "2px dashed #fde68a", borderRadius: "8px", padding: "32px", textAlign: "center", marginBottom: "16px", transition: "border-color 0.2s, background 0.2s" },
  fileIcon: { fontSize: "32px", marginBottom: "8px" },
  fileZoneText: { color: "#111827", fontSize: "14px", fontWeight: "600", marginBottom: "4px" },
  fileZoneSub: { color: "#9ca3af", fontSize: "12px" },
  fileListWrap: { marginBottom: "16px" },
  fileListHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" },
  fileListHint: { color: "#9ca3af", fontSize: "11px" },
  fileItem: { display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: "6px", marginBottom: "6px" },
  dragHandle: { color: "#9ca3af", cursor: "grab", fontSize: "16px", userSelect: "none" },
  fileNum: { color: "#9ca3af", fontSize: "12px", minWidth: "18px", textAlign: "center" },
  fileName: { color: "#111827", fontSize: "13px", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  fileSize: { color: "#6b7280", fontSize: "11px", whiteSpace: "nowrap" },
  removeBtn: { background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: "13px", padding: "2px 4px" },
  progressWrap: { marginBottom: "12px" },
  progressBar: { height: "4px", background: "#e5e7eb", borderRadius: "2px", overflow: "hidden", marginBottom: "6px" },
  progressFill: { height: "100%", background: "linear-gradient(90deg, #1e6fd9, #60a5fa)", transition: "width 0.3s ease", borderRadius: "2px" },
  progressText: { color: "#6b7280", fontSize: "12px" },
  error: { background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.4)", borderRadius: "6px", color: "#dc2626", fontSize: "13px", padding: "10px 14px" },
  footer: { padding: "16px 24px", borderTop: "1px solid #fde68a", display: "flex", justifyContent: "flex-end", gap: "10px" },
  btn: { padding: "10px 24px", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer", border: "none", fontFamily: "inherit", transition: "opacity 0.2s" },
  cancelBtn: { background: "linear-gradient(135deg, #ef4444, #dc2626)", color: "#fff" },
  uploadBtn: { background: "linear-gradient(135deg, #1e6fd9, #0d4fa8)", color: "#fff" },
};
